import { Badge, Fade, Grid, Stack } from "@mui/material";
import Typography from "common/components/Typography";
import React, { useContext, useEffect, useState } from "react";
import { DraftPoolEntry, RoomContext } from "App/context";
import { Link, useNavigate } from "react-router-dom";
import { useSocket } from "common/hooks/useSocket";
import { PlayersNumber, Role, Room, RoomState } from "types/Room/type";
import SelectPlayer from "./SelectPlayer";
import SpectatorsList from "./SpectatorsList";
import { useTranslation } from "react-i18next";
import SkewButton from "common/components/SkewButton";
import GenshinButton from "common/components/GenshinButton";
import BalancingBans from "./BalancingBans";
import {
  BalancingBansContext,
  BalancingBansProvider,
} from "./BalancingBans/context";
import {
  BannedCharacter,
  SelectingBan,
} from "common/components/DraftCharacterCard";
import FlipCoin from "./FlipCoin";
import FirstPartBansSelector, {
  MAX_FIRST_PART_BANS,
  MIN_FIRST_PART_BANS,
  countFirstPartBans,
  withFirstPartBans,
} from "./FirstPartBansSelector";
import { JokerBansContext, JokerBansProvider } from "./JokerBans/context";
import JokerBans from "./JokerBans";
import { DraftParams } from "service/draft/type";
import { getDraftParams, getScore } from "service/draft";
import ParamsDialog from "./ParamsDialog";
import TeamScore from "./TeamScore";
import { Player } from "types/Player/type";
import { getPlayers } from "service/player";
import { useErrorNotification } from "common/components/Notification";
import { Player as PlayerBox } from "service/player/type";
import TimerDraftSelector from "./TimerDraftSelector";
import SoloBanSide from "./SoloBanSide";

// Siège N = rôle "Player N" ; J1 et J2 sont les capitaines, J3 et J4 leurs coéquipiers.
type SeatNumber = 1 | 2 | 3 | 4;

const usePlayerScore = (
  player: Player | undefined,
  score: number | undefined,
  setScore: React.Dispatch<React.SetStateAction<number | undefined>>,
  sheetId: number,
) => {
  useEffect(() => {
    if (!player) {
      setScore(undefined);
      return;
    }

    if (score !== undefined) return;

    let cancelled = false;

    const fetchScore = async () => {
      const result = await getScore(player.box ?? [], sheetId);
      if (!cancelled) {
        setScore(result);
      }
    };

    fetchScore();

    return () => {
      cancelled = true;
    };
  }, [player, score, sheetId, setScore]);
};

/*
  Score d'une équipe, seulement quand elle est complète. Une réponse arrivée après un départ
  ou un changement de composition est ignorée : elle réaffichait un score périmé, sur lequel
  l'admin recalculait les bans de 1ère partie.
*/
const useTeamScore = (
  complete: boolean,
  teamBox: Player["box"] | undefined,
  setScore: React.Dispatch<React.SetStateAction<number | undefined>>,
  sheetId: number,
) => {
  // Chaque update-room renvoie un nouveau tableau : on ne réagit qu'à un vrai changement.
  const teamBoxKey = JSON.stringify(teamBox);
  useEffect(() => {
    if (!complete) {
      setScore(undefined);
      return;
    }

    let cancelled = false;
    const fetchScore = async () => {
      const result = await getScore(teamBox ?? [], sheetId);
      if (!cancelled) setScore(result);
    };

    fetchScore();
    return () => {
      cancelled = true;
    };
  }, [complete, teamBoxKey, sheetId, setScore]);
};

const WaitingRoom = () => {
  const { t } = useTranslation();
  const socket = useSocket();
  const navigate = useNavigate();
  const {
    spectators,
    dispatch,
    admin,
    draftModel,
    player1,
    player2,
    player3,
    player4,
    balancingBansPlayer1,
    jokerBansPlayer1,
    balancingBansPlayer2,
    jokerBansPlayer2,
    params,
    role,
    localPlayer,
    teamBoxPlayer1,
    teamBoxPlayer2,
  } = useContext(RoomContext);
  const {
    dispatch: dispatchBalancingBansContext,
    selectingBans: selectingBalancingBans,
    selectedBansPlayer1: selectedBalancingBansPlayer1,
    selectedBansPlayer2: selectedBalancingBansPlayer2,
  } = useContext(BalancingBansContext);
  const {
    dispatch: dispatchJokerBansContext,
    selectingBans: selectingJokerBans,
    selectedBansPlayer1: selectedJokerBansPlayer1,
    selectedBansPlayer2: selectedJokerBansPlayer2,
  } = useContext(JokerBansContext);
  const [openParams, setOpenParams] = useState<boolean>(false);
  const [startingDraft, setStartingDraft] = useState<boolean>(false);
  const [draftParams, setDraftParams] = useState<DraftParams>();
  // Solo : camp choisi pour recevoir les bans pré-draft (les deux familles).
  const [chosenBanSide, setSoloBanSide] = useState<"Player 1" | "Player 2">();
  const [playersList, setPlayersList] = useState<PlayerBox[]>([]);
  const dispatchError = useErrorNotification();
  const [scoreP1, setScoreP1] = useState<number | undefined>();
  const [scoreP2, setScoreP2] = useState<number | undefined>();
  const [scoreP3, setScoreP3] = useState<number | undefined>();
  const [scoreP4, setScoreP4] = useState<number | undefined>();
  const [scoreBlueTeam, setScoreBlueTeam] = useState<number | undefined>();
  const [scoreRedTeam, setScoreRedTeam] = useState<number | undefined>();
  const is1VS1 = params.playersNumber === PlayersNumber._1vs1;
  const is2VS2 = params.playersNumber === PlayersNumber._2vs2;
  const isSolo = params.solo === true;
  // Sans choix explicite : le camp qui a déjà des bans, sinon celui que l'équilibrage désigne.
  const soloBanSide: "Player 1" | "Player 2" =
    chosenBanSide ??
    (balancingBansPlayer1 || jokerBansPlayer1
      ? "Player 1"
      : balancingBansPlayer2 || jokerBansPlayer2
        ? "Player 2"
        : (draftParams?.balancingBans ?? 0) < 0
          ? "Player 1"
          : "Player 2");
  // Tous les sièges du format sont occupés : condition des bans pré-draft et du démarrage.
  const seatsComplete =
    player1 !== undefined &&
    player2 !== undefined &&
    (is1VS1 || (player3 !== undefined && player4 !== undefined));

  const resetBanContexts = () => {
    dispatchBalancingBansContext({
      selectingBans: undefined,
      selectedBansPlayer1: undefined,
      selectedBansPlayer2: undefined,
    });
    dispatchJokerBansContext({
      selectingBans: undefined,
      selectedBansPlayer1: undefined,
      selectedBansPlayer2: undefined,
    });
  };

  // La room attend un joueur au format socket : la box s'appelle `box`, pas `characters`.
  const toRoomPlayer = (player: PlayerBox, id?: number): Player => ({
    id,
    pseudo: player.pseudo,
    avatar: player.avatar,
    box: player.characters,
    preferences: player.preferences ?? [],
  });

  /*
    SOLO — sur son propre siège, le joueur prend la box d'un autre joueur en gardant son
    identité ; sur tout autre siège (adversaires comme coéquipier), il installe le joueur qu'il
    veut simuler.
  */
  const onChangeSeatPlayer =
    (
      seat: SeatNumber,
      setScore: React.Dispatch<React.SetStateAction<number | undefined>>,
    ) =>
    (player: PlayerBox) => {
      setScore(undefined);
      resetBanContexts();
      if (role === `Player ${seat}`)
        socket.emit(
          "on-change-player-box",
          toRoomPlayer(player, localPlayer?.id),
        );
      else
        socket.emit("send-solo-player", seat, toRoomPlayer(player, player.id));
    };

  const isCaptain = role === "Player 1" || role === "Player 2";
  const scoreSetters = [setScoreP1, setScoreP2, setScoreP3, setScoreP4];
  /*
    SOLO — seuls les capitaines draftent : le joueur ne peut s'asseoir qu'en J1 ou J2 (le
    serveur refuse J3/J4). Tant qu'il n'a pas choisi son camp, les sièges des coéquipiers sont
    fermés ; une fois assis, tous les autres sièges se remplissent avec des joueurs simulés.
    Repasser spectateur les vide tous.
  */
  const soloSeatProps = (seat: SeatNumber) => ({
    onClickChangePlayer: onChangeSeatPlayer(seat, scoreSetters[seat - 1]),
    editable: isSolo && isCaptain,
    simulateOnEmpty: isSolo && isCaptain && role !== `Player ${seat}`,
    joinDisabled: isSolo && seat > 2,
  });

  type PlayerSlot = {
    key: string;
    player?: Player;
    score?: number;
    onClick: () => void;
    onClickChangePlayer?: (player: PlayerBox) => void;
    onSearchChange: (search: string) => void;
    visible: boolean;
    editable?: boolean;
    simulateOnEmpty?: boolean;
    joinDisabled?: boolean;
  };

  const leftPlayerSlots: PlayerSlot[] = [
    {
      key: "player1",
      player: player1,
      score: scoreP1,
      onClick: () => socket.emit("select-player1"),
      onSearchChange: (search) => fetchPlayers(search),
      visible: true,
      ...soloSeatProps(1),
    },
    {
      key: "player3",
      player: player3,
      score: scoreP3,
      onClick: () => socket.emit("select-player3"),
      onSearchChange: (search) => fetchPlayers(search),
      visible: is2VS2,
      ...soloSeatProps(3),
    },
  ];

  const rightPlayerSlots: PlayerSlot[] = [
    {
      key: "player2",
      player: player2,
      score: scoreP2,
      onClick: () => socket.emit("select-player2"),
      onSearchChange: (search) => fetchPlayers(search),
      visible: true,
      ...soloSeatProps(2),
    },
    {
      key: "player4",
      player: player4,
      score: scoreP4,
      onClick: () => socket.emit("select-player4"),
      onSearchChange: (search) => fetchPlayers(search),
      visible: is2VS2,
      ...soloSeatProps(4),
    },
  ];

  const renderBanSection = (
    title: string,
    selectedBans:
      | typeof selectedBalancingBansPlayer1
      | typeof selectedJokerBansPlayer1
      | undefined,
    bans: typeof balancingBansPlayer1 | typeof jokerBansPlayer1 | undefined,
  ) =>
    selectedBans || bans ? (
      <>
        <Typography size="sm">{title}</Typography>
        <Stack
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(85px, 1fr))"
          gap={1}
        >
          {selectedBans?.map((char) => (
            <SelectingBan key={char.name} character={char} />
          ))}
          {bans?.map((char) => (
            <BannedCharacter key={char.name} character={char} />
          ))}
        </Stack>
      </>
    ) : null;

  const fetchPlayers = async (search: string = "") => {
    try {
      const data = await getPlayers(1, 10, search);
      setPlayersList(data.data);
    } catch (e) {
      dispatchError(e as string);
    }
  };

  const startDraftTransition = () => setStartingDraft(true);

  const subscribeEvents = () => {
    socket.on("update-role", (role: Role) => dispatch({ role }));
    socket.on("start-draft", startDraftTransition);
    socket.on(
      "room-state",
      (state: RoomState["state"]) =>
        (state === "InDraft" || state === "EndDraft") &&
        startDraftTransition(),
    );
    socket.on("update-room", (room: Room & { draftStarted: boolean }) => {
      dispatch({
        ...room,
        player1: room.player1 ?? undefined,
        player2: room.player2 ?? undefined,
        player3: room.player3 ?? undefined,
        player4: room.player4 ?? undefined,
        teamBoxPlayer1: room.teamBoxPlayer1 ?? undefined,
        teamBoxPlayer2: room.teamBoxPlayer2 ?? undefined,
        balancingBansPlayer1: room.balancingBansPlayer1 ?? undefined,
        jokerBansPlayer1: room.jokerBansPlayer1 ?? undefined,
        balancingBansPlayer2: room.balancingBansPlayer2 ?? undefined,
        jokerBansPlayer2: room.jokerBansPlayer2 ?? undefined,
      });
    });
    socket.on("balanced-ban", (room: Room) => {
      dispatch({
        ...room,
        jokerBansPlayer1: undefined,
        jokerBansPlayer2: undefined,
      });
      dispatchBalancingBansContext({
        selectingBans: undefined,
        selectedBansPlayer1: undefined,
        selectedBansPlayer2: undefined,
      });
    });
    socket.on("joker-ban", (room: Room) => {
      // En solo, des bans joker effacent les bans d'équilibrage de l'autre camp : un champ
      // absent du payload doit vider l'état local, pas le laisser tel quel.
      dispatch({
        ...room,
        balancingBansPlayer1: room.balancingBansPlayer1 ?? undefined,
        balancingBansPlayer2: room.balancingBansPlayer2 ?? undefined,
        jokerBansPlayer1: room.jokerBansPlayer1 ?? undefined,
        jokerBansPlayer2: room.jokerBansPlayer2 ?? undefined,
      });
      dispatchJokerBansContext({
        selectingBans: undefined,
        selectedBansPlayer1: undefined,
        selectedBansPlayer2: undefined,
      });
    });
    socket.on("admin-left", () => {
      navigate("/");
    });
    // Même pool que pendant la draft : la grille des bans négociés s'en sert aussi.
    socket.on("draft-pool", (draftPool: DraftPoolEntry[]) =>
      dispatch({ draftPool: draftPool ?? [] }),
    );
  };

  useEffect(() => {
    subscribeEvents();
    socket.emit("get-room");
    socket.emit("get-role");
    socket.emit("get-room-state");

    fetchPlayers();

    return () => {
      socket.removeListener("update-role");
      socket.removeListener("start-draft");
      socket.removeListener("room-state");
      socket.removeListener("update-room");
      socket.removeListener("balanced-ban");
      socket.removeListener("joker-ban");
      socket.removeListener("admin-left");
      socket.removeListener("draft-pool");
    };
  }, []);

  usePlayerScore(player1, scoreP1, setScoreP1, params.sheet.id);
  usePlayerScore(player2, scoreP2, setScoreP2, params.sheet.id);
  usePlayerScore(player3, scoreP3, setScoreP3, params.sheet.id);
  usePlayerScore(player4, scoreP4, setScoreP4, params.sheet.id);

  const hasBlueTeam = is2VS2 && player1 && player3;
  const hasRedTeam = is2VS2 && player2 && player4;

  // La box d'équipe est calculée par le serveur : l'union des box des joueurs de l'équipe.
  useTeamScore(!!hasBlueTeam, teamBoxPlayer1, setScoreBlueTeam, params.sheet.id);
  useTeamScore(!!hasRedTeam, teamBoxPlayer2, setScoreRedTeam, params.sheet.id);

  // Chaque update-room renvoie de nouveaux tableaux : on ne réagit qu'à un vrai changement.
  const teamBoxesKey = JSON.stringify([teamBoxPlayer1, teamBoxPlayer2]);
  useEffect(() => {
    const shouldComputeDraftParams =
      (is1VS1 && scoreP1 !== undefined && scoreP2 !== undefined) ||
      (is2VS2 && scoreBlueTeam !== undefined && scoreRedTeam !== undefined);

    if (
      !shouldComputeDraftParams ||
      teamBoxPlayer1 === undefined ||
      teamBoxPlayer2 === undefined
    ) {
      setDraftParams(undefined);
      return;
    }

    // Une réponse arrivée après un changement d'équipe ne doit pas écraser l'état courant.
    let cancelled = false;
    const checkDraftParams = async () => {
      const result = await getDraftParams(
        teamBoxPlayer1,
        teamBoxPlayer2,
        params.sheet.id,
      );
      if (!cancelled) setDraftParams(result);
    };

    checkDraftParams();
    return () => {
      cancelled = true;
    };
  }, [
    is1VS1,
    is2VS2,
    scoreP1,
    scoreP2,
    scoreBlueTeam,
    scoreRedTeam,
    teamBoxesKey,
    params.sheet.id,
  ]);

  /*
    Dès que l'API recommande un nombre de bans en 1ère partie, le modèle s'y aligne. Seul
    l'admin émet, sinon chaque client de la room enverrait la même mise à jour. Le réglage reste
    modifiable à la main ensuite, jusqu'à la prochaine recommandation.
  */
  useEffect(() => {
    const recommended = draftParams?.firstPartBans;
    if (!admin || recommended === undefined) return;
    const value = Math.min(
      MAX_FIRST_PART_BANS,
      Math.max(MIN_FIRST_PART_BANS, recommended),
    );
    if (value !== countFirstPartBans(draftModel))
      socket.emit("update-draft-model", withFirstPartBans(draftModel, value));
  }, [draftParams]);

  return (
    <Fade
      in={!startingDraft}
      timeout={400}
      onExited={() => navigate("/draft")}
      unmountOnExit
    >
      <Grid
        container
        direction="column"
        alignItems="center"
        height="100%"
        flexWrap="nowrap"
        sx={{
          backgroundImage: "linear-gradient(#000B, #000B), url(/got.png)",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
      <Typography size="xl">{t("Salle d'attente")}</Typography>
      <Grid item height="100%" width="100%" flexGrow={1}>
        <Stack
          direction="row"
          gap={3}
          gridTemplateColumns="23rem auto 23rem"
          display="grid"
          padding={1}
          paddingTop="0"
          height="100%"
        >
          <Stack direction="column" justifyContent="space-between">
            <Stack gap={1}>
              {is2VS2 && (
                <TeamScore score={scoreBlueTeam} color="player1" />
              )}
              {leftPlayerSlots.map(
                ({
                  key,
                  player,
                  score,
                  onClick,
                  onSearchChange,
                  onClickChangePlayer,
                  visible,
                  editable,
                  simulateOnEmpty,
                  joinDisabled,
                }) =>
                  visible ? (
                    <SelectPlayer
                      key={key}
                      player={player}
                      playersList={playersList}
                      color={"player1"}
                      score={score}
                      onClick={onClick}
                      onSearchChange={onSearchChange}
                      onClickChangePlayer={onClickChangePlayer ?? (() => {})}
                      editable={editable}
                      simulateOnEmpty={simulateOnEmpty}
                      joinDisabled={joinDisabled}
                    />
                  ) : null,
              )}
              {renderBanSection(
                t("Bans d'équilibrage :"),
                selectedBalancingBansPlayer1,
                balancingBansPlayer1,
              )}
              {renderBanSection(
                t("Bans joker :"),
                selectedJokerBansPlayer1,
                jokerBansPlayer1,
              )}
            </Stack>
            <Link to={"/"}>
              <GenshinButton variant="contained">{t("Quitter")}</GenshinButton>
            </Link>
          </Stack>

          <Stack direction="column" alignItems="center" gap={1}>
            <GenshinButton onClick={() => setOpenParams(true)} sx={{ mb: 0.5 }}>
              {t("Informations du salon")}
            </GenshinButton>
            <Stack direction="row" gap={1} alignItems="center">
              <TimerDraftSelector />
              <Badge
                color={"warning"}
                badgeContent={draftParams?.firstPartBans}
              >
                <FirstPartBansSelector />
              </Badge>
            </Stack>
            {!isSolo && <FlipCoin display={!seatsComplete} />}
            {isSolo &&
              isCaptain &&
              seatsComplete &&
              selectingBalancingBans === undefined &&
              selectingJokerBans === undefined && (
                <SoloBanSide value={soloBanSide} onChange={setSoloBanSide} />
              )}
            <Stack
              direction="row"
              height="100%"
              gap={1}
              alignItems="center"
              width={
                selectedJokerBansPlayer1 !== undefined ||
                selectedJokerBansPlayer2 !== undefined ||
                selectedBalancingBansPlayer1 !== undefined ||
                selectedBalancingBansPlayer2 !== undefined
                  ? "100%"
                  : undefined
              }
            >
              {seatsComplete &&
                selectedJokerBansPlayer1 === undefined &&
                selectedJokerBansPlayer2 === undefined && (
                  <BalancingBans
                    badgeQuantity={draftParams?.balancingBans}
                    whoBan={
                      (draftParams?.balancingBans ?? 0) < 0
                        ? "player1"
                        : "player2"
                    }
                    soloSide={soloBanSide}
                  />
                )}
              {seatsComplete &&
                selectedBalancingBansPlayer1 === undefined &&
                selectedBalancingBansPlayer2 === undefined && (
                  <JokerBans
                    badgeQuantity={draftParams?.jokerBans}
                    whoBan={
                      (draftParams?.jokerBans ?? 0) < 0 ? "player1" : "player2"
                    }
                    soloSide={soloBanSide}
                  />
                )}
            </Stack>
            {admin && (
              <SkewButton
                variant="contained"
                disabled={
                  // Le serveur refuse aussi une draft avec une équipe incomplète.
                  !seatsComplete ||
                  selectingBalancingBans !== undefined ||
                  selectingJokerBans !== undefined
                }
                onClick={() => socket.emit("send-start-draft")}
                sx={{
                  width: "fit-content",
                  margin: "auto",
                  padding: "0.75rem 5rem",
                }}
              >
                {t("Commencer")}
              </SkewButton>
            )}
          </Stack>

          <Stack direction="column" justifyContent="space-between">
            <Stack gap={1}>
              {is2VS2 && (
                <TeamScore score={scoreRedTeam} color="player2" />
              )}
              {rightPlayerSlots.map(
                ({
                  key,
                  player,
                  score,
                  onClick,
                  onSearchChange,
                  onClickChangePlayer,
                  visible,
                  editable,
                  simulateOnEmpty,
                  joinDisabled,
                }) =>
                  visible ? (
                    <SelectPlayer
                      key={key}
                      player={player}
                      playersList={playersList}
                      color={"player2"}
                      score={score}
                      onClick={onClick}
                      onSearchChange={onSearchChange}
                      onClickChangePlayer={onClickChangePlayer ?? (() => {})}
                      editable={editable}
                      simulateOnEmpty={simulateOnEmpty}
                      joinDisabled={joinDisabled}
                    />
                  ) : null,
              )}
              {renderBanSection(
                t("Bans d'équilibrage :"),
                selectedBalancingBansPlayer2,
                balancingBansPlayer2,
              )}
              {renderBanSection(
                t("Bans joker :"),
                selectedJokerBansPlayer2,
                jokerBansPlayer2,
              )}
            </Stack>
            <SpectatorsList
              spectators={spectators}
              onClick={() => socket.emit("select-spectator")}
            />
          </Stack>
        </Stack>
      </Grid>
      <ParamsDialog open={openParams} onClose={() => setOpenParams(false)} />
      </Grid>
    </Fade>
  );
};

const Wrapper = () => {
  return (
    <BalancingBansProvider>
      <JokerBansProvider>
        <WaitingRoom />
      </JokerBansProvider>
    </BalancingBansProvider>
  );
};

export default Wrapper;
